import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import CountrySelect from '../components/CountrySelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Zap, Eye, EyeOff, Moon, Sun, Globe, AlertCircle, Loader2
} from 'lucide-react';
import ZitiusLogo from '../components/brand/ZitiusLogo';
import StravaPoweredByBadge from '../components/brand/StravaPoweredByBadge';
import api from '../utils/axiosConfig';

const DEV_LOGIN_DEFAULTS =
  process.env.NODE_ENV === 'development'
    ? { email: 'frode@sc.no', password: 'c' }
    : { email: '', password: '' };

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState(DEV_LOGIN_DEFAULTS.email);
  const [password, setPassword] = useState(DEV_LOGIN_DEFAULTS.password);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('athlete');
  const [countryId, setCountryId] = useState(null);
  const [birthdate, setBirthdate] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, register } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { mode, toggleMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLogin && !countryId) {
      api.get('/api/countries').then((res) => {
        const no = (res.data.countries || []).find((c) => c.code === 'NO');
        if (no) setCountryId(no.id);
      }).catch(() => {});
    }
  }, [isLogin, countryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsSubmitting(false);
          return;
        }
        if (!countryId) {
          setError(t('auth.country') + ' is required');
          setIsSubmitting(false);
          return;
        }
        await register(email, password, name, role, countryId, birthdate || null);
      }
      const intended = sessionStorage.getItem('intendedRoute') || '/dashboard';
      sessionStorage.removeItem('intendedRoute');
      navigate(intended, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || (isLogin ? t('auth.loginError') : t('auth.registerError'));
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='relative min-h-screen overflow-hidden bg-[hsl(var(--brand-navy))] text-white'>
      <div
        className='absolute inset-0 bg-cover bg-center'
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(5, 11, 24, 0.74) 0%, rgba(5, 11, 24, 0.54) 42%, rgba(5, 11, 24, 0.84) 100%), url('/brand/illustrations/zitius-community-hero.png')",
        }}
      />
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(30,151,244,0.28),transparent_30%),radial-gradient(circle_at_right,rgba(7,20,48,0.82),transparent_44%)]' />

      <div className='relative z-10 min-h-screen'>
        <div className='flex items-center justify-between gap-2 p-4 sm:px-6'>
          <Button
            asChild
            variant='ghost'
            className='rounded-full text-white hover:bg-white/10 hover:text-white'
          >
            <Link to='/'>{t('marketing.backToHome')}</Link>
          </Button>

          <div className='flex items-center gap-2'>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => setLanguage(language === 'en' ? 'no' : 'en')}
              title={t('nav.language')}
              className='text-white hover:bg-white/10 hover:text-white'
            >
              <Globe className='h-4 w-4' />
            </Button>
            <span className='text-xs uppercase text-white/72'>{language}</span>

            <Button
              variant='ghost'
              size='icon'
              onClick={toggleMode}
              title={mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
              className='text-white hover:bg-white/10 hover:text-white'
            >
              {mode === 'dark' ? <Sun className='h-4 w-4' /> : <Moon className='h-4 w-4' />}
            </Button>
          </div>
        </div>

        <div className='mx-auto flex min-h-[calc(100vh-72px)] max-w-7xl flex-col gap-10 px-6 pb-10 pt-6 lg:flex-row lg:items-center lg:gap-14 lg:px-8 lg:pb-14'>
          <div className='flex flex-1 items-center'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className='max-w-xl'
            >
              <div className='mb-8'>
                <ZitiusLogo size="lg" variant="light" className="mb-3" />
                <p className='max-w-md text-sm leading-6 text-white/78'>{t('marketing.loginLead')}</p>
              </div>

              <div className='space-y-6 max-w-md'>
                <Feature
                  title={t('marketing.hero.cards.trackTitle')}
                  description={t('marketing.hero.cards.trackDescription')}
                />
                <Feature
                  title={t('marketing.hero.cards.coachTitle')}
                  description={t('marketing.hero.cards.coachDescription')}
                />
                <Feature
                  title={t('marketing.hero.cards.aiTitle')}
                  description={t('marketing.hero.cards.aiDescription')}
                />
              </div>
            </motion.div>
          </div>

          <div className='flex w-full justify-end lg:max-w-[480px] lg:min-w-[440px]'>
            <motion.div
              className='w-full max-w-md lg:max-w-[460px]'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className='mb-8 lg:hidden'>
                <ZitiusLogo size="md" variant="light" />
              </div>

              <div className='mb-8'>
                <h2 className='text-2xl font-bold text-white'>
                  {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
                </h2>
                <p className='mt-1 text-white/68'>
                  {isLogin ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}
                </p>
              </div>

              <Card className='border border-white/10 bg-[rgba(13,22,46,0.48)] shadow-[0_30px_80px_rgba(2,8,20,0.4)]'>
                <CardContent className='pt-6'>
                <AnimatePresence mode='wait'>
                  <motion.form
                    key={isLogin ? 'login' : 'register'}
                    initial={{ opacity: 0, x: isLogin ? -10 : 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: isLogin ? 10 : -10 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleSubmit}
                    className='space-y-4'
                  >
                    {!isLogin && (
                      <div className='space-y-2'>
                        <Label htmlFor='name'>{t('auth.name')}</Label>
                        <Input
                          id='name'
                          type='text'
                          placeholder='John Doe'
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required={!isLogin}
                          autoComplete='name'
                        />
                      </div>
                    )}

                    <div className='space-y-2'>
                      <Label htmlFor='email'>{t('auth.email')}</Label>
                      <Input
                        id='email'
                        type='email'
                        placeholder='you@example.com'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete='email'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='password'>{t('auth.password')}</Label>
                      <div className='relative'>
                        <Input
                          id='password'
                          type={showPassword ? 'text' : 'password'}
                          placeholder='••••••••'
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          autoComplete={isLogin ? 'current-password' : 'new-password'}
                        />
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='absolute right-0 top-0 h-full px-3 hover:bg-transparent'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword
                            ? <EyeOff className='h-4 w-4 text-muted-foreground' />
                            : <Eye className='h-4 w-4 text-muted-foreground' />
                          }
                        </Button>
                      </div>
                      {!isLogin && (
                        <p className='text-xs text-white/55'>
                          {t('auth.passwordRequirements')}
                        </p>
                      )}
                    </div>

                    {!isLogin && (
                      <>
                        <div className='space-y-2'>
                          <Label htmlFor='confirmPassword'>{t('auth.confirmPassword')}</Label>
                          <Input
                            id='confirmPassword'
                            type='password'
                            placeholder='••••••••'
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required={!isLogin}
                            autoComplete='new-password'
                          />
                        </div>

                        <div className='space-y-2'>
                          <Label>{t('auth.country')} *</Label>
                          <CountrySelect
                            value={countryId}
                            onChange={setCountryId}
                            placeholder={t('auth.country')}
                            required
                          />
                        </div>

                        <div className='space-y-2'>
                          <Label htmlFor='birthdate'>{t('auth.birthdate')}</Label>
                          <Input
                            id='birthdate'
                            type='date'
                            value={birthdate}
                            onChange={(e) => setBirthdate(e.target.value)}
                            autoComplete='bday'
                          />
                        </div>

                        <div className='space-y-2'>
                          <Label>{t('auth.role')}</Label>
                          <Select value={role} onValueChange={setRole}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='athlete'>{t('auth.roleAthlete')}</SelectItem>
                              <SelectItem value='coach'>{t('auth.roleCoach')}</SelectItem>
                              <SelectItem value='podcast_manager'>{t('auth.rolePodcastManager')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className='flex items-center gap-2 rounded-md bg-destructive/15 p-3 text-sm text-red-200'
                      >
                        <AlertCircle className='h-4 w-4 shrink-0' />
                        {error}
                      </motion.div>
                    )}

                    <Button
                      type='submit'
                      className='w-full'
                      size='lg'
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-2' />
                      ) : null}
                      {isLogin ? t('auth.login') : t('auth.register')}
                    </Button>
                  </motion.form>
                </AnimatePresence>

                <Separator className='my-6 bg-white/10' />

                <p className='text-center text-sm text-white/55'>
                  {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
                  <button
                    type='button'
                    className='font-medium text-[hsl(var(--brand-electric-soft))] hover:underline'
                    onClick={() => { setIsLogin(!isLogin); setError(''); }}
                  >
                    {isLogin ? t('auth.signUp') : t('auth.signIn')}
                  </button>
                </p>
              </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      <StravaPoweredByBadge className='absolute bottom-4 right-4 z-20 sm:bottom-6 sm:right-6' />
    </div>
  );
};

const Feature = ({ title, description }) => (
  <div className='flex items-start gap-3'>
    <div className='w-8 h-8 rounded-lg bg-white/12 flex items-center justify-center shrink-0 mt-0.5'>
      <Zap className='w-4 h-4' />
    </div>
    <div>
      <h3 className='font-semibold text-sm'>{title}</h3>
      <p className='text-sm text-white/72'>{description}</p>
    </div>
  </div>
);

export default LoginPage;
